import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useChildren } from "@/hooks/useChildren";
import { useAuth } from "@/hooks/useAuth";
import { useTribeMembers } from "@/hooks/useTribeMembers";
import { useCurrentRole } from "@/hooks/useCurrentRole";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useCaptureContext } from "@/contexts/CaptureContext";
import { LocationInput } from "@/components/capture/LocationInput";
import VisibilityReadout from "@/components/capture/VisibilityReadout";
import ShareWithTaggedToggle from "@/components/capture/ShareWithTaggedToggle";
import { useCollections } from "@/hooks/useCollections";
import ParticipantPicker from "@/components/ParticipantPicker";
import CollectionPicker from "@/components/CollectionPicker";
import { AddEditSubjectSheet } from "@/components/AddEditSubjectSheet";
import type { ParticipantRef } from "@/types/participant";
import type { Subject } from "@/lib/supabase";

export default function MemoryMetadataScreen() {
  const { t } = useTranslation();
  const { data, completeMetadataStep, cancel, goBack } = useCaptureContext();
  const { user } = useAuth();
  const { role: currentRole, spaceMemberId } = useCurrentRole();
  const { tier } = useEntitlements();
  const isChild = currentRole === "child";
  const { subjects: allSubjects } = useChildren();
  // When a Kindle-kid is logged in, filter out their own children-table entry —
  // they're already represented by the ME chip; showing both creates a duplicate.
  const taggableSubjects = useMemo(
    () =>
      spaceMemberId
        ? allSubjects.filter((s) => s.promoted_to_space_member_id !== spaceMemberId)
        : allSubjects,
    [allSubjects, spaceMemberId],
  );
  const [addPetOpen, setAddPetOpen] = useState(false);
  const { coparents } = useTribeMembers();
  const { collections, activeCollection, refresh: refreshCollections } = useCollections();

  // ── Pre-fill source for the Collection picker ────────────────────────────
  // Priority: card context (explicit intent from CollectionDetailSheet /
  // feed card) > active-capture mode > none. When both are present, the
  // feed-card wins and we surface the override as microcopy.
  // An explicit `null` on data.collectionId means the user opted out of the
  // active collection upstream (e.g. from the CaptureTypePicker pill) — skip
  // the active-mode fallback in that case.
  const explicitOptOut =
    "collectionId" in data && data.collectionId === null;
  const prefillCollectionId = explicitOptOut
    ? null
    : data.collectionId ?? activeCollection?.id ?? null;
  const prefillSource: "active" | "card" | null = explicitOptOut
    ? null
    : data.collectionId
      ? "card"
      : activeCollection
        ? "active"
        : null;

  const [pickedCollectionId, setPickedCollectionId] = useState<string | null>(
    prefillCollectionId,
  );
  // Keep the picker's value in sync if the capture context resolves late
  // (e.g. activeCollection fetched after mount). Only auto-updates while the
  // user hasn't overridden the pre-fill — once they've picked something
  // different, their choice wins.
  const didUserOverride = useRef(false);
  useEffect(() => {
    if (didUserOverride.current) return;
    setPickedCollectionId(prefillCollectionId);
  }, [prefillCollectionId]);

  const handlePickerChange = (id: string | null) => {
    didUserOverride.current = true;
    setPickedCollectionId(id);
  };

  const selectedCollection = useMemo(
    () =>
      pickedCollectionId
        ? collections.find((c) => c.id === pickedCollectionId) ?? null
        : null,
    [pickedCollectionId, collections],
  );

  // Collection color wins; falls back to brand sage. Re-sources from the
  // picker's current value so the save button tint follows user overrides.
  const accentColor =
    selectedCollection?.color ??
    data.collectionColor ??
    activeCollection?.color ??
    "#7A9A7C";

  // Subject IDs (children + pets, later toys). Stored under the legacy
  // `childIds` name because the DB column is still memory_participants.child_id.
  const [childIds, setChildIds] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  const [memoryDate, setMemoryDate] = useState(() => new Date());
  const todayIso = new Date().toISOString().slice(0, 10);
  const memoryDateIso = memoryDate.toISOString().slice(0, 10);
  // Everyone starts deselected — the user must actively tag who was there.
  const [participants, setParticipants] = useState<ParticipantRef[]>([]);

  // At least one subject (child OR person) must be tagged on every memory.
  const subjectCount = childIds.length + participants.length;
  const canSave = subjectCount > 0;

  // Tagged non-members = people whose access depends on audience='space'.
  // Two groups qualify:
  //   1. `connection` + role='contributor' — see the memory today when tagged.
  //   2. Tagged children with an ACTIVE Kindle-kid login — see the memory
  //      today via the `memories_child_select` RLS branch (20260424140000).
  // Children without an account are subjects only — toggling audience has
  // no observable effect on them, so they're excluded from the toggle's
  // gate and subtext. Owner is always the creator (unaffected) and
  // `member` sees everything regardless of audience, so neither belongs
  // on this toggle. Free-text `name` refs aren't users at all.
  const taggedContributors = useMemo(
    () =>
      participants.filter(
        (p): p is Extract<ParticipantRef, { kind: "connection" }> =>
          p.kind === "connection" && p.role === "contributor",
      ),
    [participants],
  );
  const taggedChildrenForShare = allSubjects.filter(
    (s) => s.subject_type === "child" && childIds.includes(s.id),
  );
  const taggedChildrenWithAccount = taggedChildrenForShare.filter(
    (c) => c.hasActiveChildAccount,
  );
  const shareableNames = [
    ...taggedContributors.map((p) => p.name),
    ...taggedChildrenWithAccount.map((c) => c.name),
  ];
  const hasShareables = shareableNames.length > 0;

  const [isSharedWithTagged, setIsSharedWithTagged] = useState(true);

  const handleSave = () => {
    if (!canSave) {
      toast.error(t("memory.metadata.selectChild"));
      return;
    }

    const isPaidTier = tier === "keeper" || tier === "storyteller";
    const isContributor = currentRole === "contributor";
    const hasVoice = !!(data.audioStoragePath || data.contextAudioPath);

    let bypass: { processingMode: "full" | "transcript_only" | "skip" } | undefined;

    if (isPaidTier) {
      bypass = { processingMode: "full" };
    } else if (isContributor) {
      bypass = { processingMode: hasVoice ? "transcript_only" : "skip" };
    }

    // Capsule guard: time_capsule reaching the gate is a dev-time invariant
    // violation (bypass should have been applied above). Force full processing
    // and log so it's visible in dev builds.
    if (data.capsuleState === "time_capsule" && !bypass) {
      if (import.meta.env.DEV) {
        console.error("[MemoryMetadataScreen] time_capsule would reach choice_gate — forcing 'full'");
      }
      bypass = { processingMode: "full" };
    }

    completeMetadataStep({
      childIds,
      participants,
      location: location || null,
      memoryDate,
      // The picker's value is authoritative (null included) — it supersedes
      // any collectionId set when capture started. Children can't link to
      // collections (RLS + product decision) so force null regardless of
      // any prefill from active-capture mode.
      collectionId: isChild ? null : pickedCollectionId,
      // Audience only matters when there's a non-member tagged whose access
      // depends on it. Otherwise default to 'space' — it's the DB default and
      // it's a no-op since no one but members+creator is reachable anyway.
      audience: hasShareables && !isSharedWithTagged ? "members" : "space",
    }, bypass);
  };

  return (
    <>
      {/* Back arrow — returns to capture step, clears capture outputs so the
          user can re-record/re-capture without losing context (collection, card).
          Anchored to the Overlay sheet (nearest `relative` ancestor). */}
      <button
        type="button"
        onClick={goBack}
        className="absolute top-3 left-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label={t("common.back")}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="w-full max-w-sm flex flex-col gap-6 px-6 py-4">
        {/* Header */}
        <div className="text-center space-y-1">
          <p className="text-xl font-semibold text-foreground">{t("memory.metadata.heading")}</p>
          <p className="text-sm text-muted-foreground">
            {t("memory.metadata.subtext")}
          </p>
        </div>

        {/* Card context banner (read-only) */}
        {data.cardContext && (
          <div
            className="rounded-xl"
            style={{ backgroundColor: "#EEEDFE", border: "0.5px solid #AFA9EC", padding: 12 }}
          >
            <p style={{ fontSize: 12, fontWeight: 500, color: "#3C3489" }}>
              {data.cardContext.title}
            </p>
          </div>
        )}

        {/* Collection picker — unified entry for picking / clearing / creating
            a collection. Pre-filled from the capture context (card > active),
            user's choice is authoritative at save.
            Hidden for child accounts: they can't write to collection_memories
            (RLS requires collection/space owner), and product-wise parents
            assign the collection during review. */}
        {!isChild && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("memory.metadata.collectionSection")}
            </p>
            <CollectionPicker
              value={pickedCollectionId}
              onChange={handlePickerChange}
              collections={collections}
              prefillSource={prefillSource}
              prefillCollectionId={prefillCollectionId}
              onCollectionCreated={refreshCollections}
            />
            {/* Microcopy — surfaces the two meaningful edge cases:
                (1) active-mode on + user cleared → opt-out confirmation;
                (2) card context + active-mode both present + user kept the
                card pick → override surfaced so they know active isn't used. */}
            {activeCollection && pickedCollectionId === null && (
              <p className="text-[11px] text-muted-foreground">
                {t("collections.picker.activeModeOptOut", {
                  activeName: activeCollection.name,
                })}
              </p>
            )}
            {activeCollection &&
              selectedCollection &&
              selectedCollection.id !== activeCollection.id &&
              prefillSource === "card" &&
              pickedCollectionId === prefillCollectionId && (
                <p className="text-[11px] text-muted-foreground">
                  {t("collections.picker.cardOverrideActive", {
                    cardName: selectedCollection.name,
                    activeName: activeCollection.name,
                  })}
                </p>
              )}
          </div>
        )}

        {/* WHO — descriptive subject picker (children + ME + tribe). Feeds
            the AI story. Does NOT affect visibility — members in the space
            see all memories regardless of who's tagged. Required — no one
            is pre-selected so the user must consciously tag everyone present. */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t("memory.metadata.whoSection")}
          </p>
          <p className="text-[11px] text-muted-foreground -mt-1">
            {t("memory.metadata.whoSectionHelper")}
          </p>
          <ParticipantPicker
            mode="memory"
            value={participants}
            onChange={setParticipants}
            allowedConnectionIds={data.collectionParticipantConnectionIds}
            taggableSubjects={taggableSubjects}
            selectedSubjectIds={childIds}
            onSubjectIdsChange={setChildIds}
            // onAddSubject={() => setAddPetOpen(true)} — hidden until contributors are deployed (see TODO.md §10)
          />
        </div>

        {/* WHERE — location */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t("memory.metadata.whereSection")}
          </p>
          <LocationInput value={location} onChange={setLocation} />
        </div>

        {/* WHEN — memory date. Defaults to today; future dates are disallowed. */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t("memory.metadata.whenSection")}
          </p>
          <input
            type="date"
            value={memoryDateIso}
            max={todayIso}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              // Parse as local midnight so the date the user picked is the
              // date stored — avoids UTC shifting it to the previous day.
              const [y, m, d] = v.split("-").map(Number);
              setMemoryDate(new Date(y, m - 1, d));
            }}
            className="w-full rounded-lg border border-border/60 bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Time capsule indicator */}
        {data.capsuleState === "time_capsule" && (
          <div
            className="rounded-lg px-3 py-2 text-[11px] text-center"
            style={{ backgroundColor: "rgba(196, 91, 75, 0.10)", color: "#8B3A2E" }}
          >
            {t("memory.metadata.timeCapsuleNote")}
          </div>
        )}

        {/* Share-with-tagged toggle — appears when at least one tagged
            non-member exists (contributor OR child). Controls the audience
            axis: ON = audience='space' (tagged contributors see today,
            tagged children see after promotion); OFF = audience='members'
            (both groups lose access). */}
        {hasShareables && (
          <ShareWithTaggedToggle
            names={shareableNames}
            checked={isSharedWithTagged}
            onCheckedChange={setIsSharedWithTagged}
          />
        )}

        {/* Visibility readout — peace-of-mind summary of who will see this
            memory once saved. Read-only; mirrors the toggle above in real time. */}
        <VisibilityReadout
          participants={participants}
          isSharedWithTagged={isSharedWithTagged}
          taggedChildren={taggedChildrenForShare.map((c) => ({
            id: c.id,
            name: c.name,
            hasActiveChildAccount: c.hasActiveChildAccount,
          }))}
        />

        {/* CTA */}
        <Button
          onClick={handleSave}
          disabled={!canSave}
          className="w-full h-12 text-base"
          style={{ backgroundColor: accentColor, color: "#FFFFFF" }}
        >
          {t("memory.metadata.saveButton")}
        </Button>

        <button
          type="button"
          onClick={cancel}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
        >
          {t("common.cancel")}
        </button>
      </div>

      {/* Inline growth-loop sheets — launched from the "+ Add" chip in the
          WHO-was-there picker. Both auto-select the newly-created entity
          on the current memory so the visibility readout and subtext
          update without a second user action. */}
      <AddEditSubjectSheet
        open={addPetOpen}
        onOpenChange={setAddPetOpen}
        subjectType="pet"
        existingSubjects={allSubjects}
        onSaved={(saved?: Subject) => {
          if (!saved) return;
          // Pets are filtered out of `shareableNames` by subject_type so
          // the share subtext invariant holds — only real children and
          // contributors contribute to "Grants access to …".
          setChildIds((prev) => (prev.includes(saved.id) ? prev : [...prev, saved.id]));
        }}
      />

    </>
  );
}
